// src/components/owner/pages/BadliModal.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Calendar, User, UserX, Layers, Timer, CheckCircle } from 'lucide-react';
import Modal from '../../ui/Modal'; 

const API_BASE = 'https://mukadampro.shirpur.store';

const BadliModal = ({ workers, onClose, onSave, handleVoiceClick, handleFocusVoice }) => {
    const [selectedWorkerId, setSelectedWorkerId] = useState('');
    const [selectedBadliWorkerId, setSelectedBadliWorkerId] = useState('');
    const [badliName, setBadliName] = useState('');
    const [selectedLooms, setSelectedLooms] = useState([]);
    
    const [currentDateView, setCurrentDateView] = useState(new Date()); 
    const [rangeStart, setRangeStart] = useState(new Date()); 
    const [rangeEnd, setRangeEnd] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false); 
    
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const prevWorkerIdRef = useRef(null);
    const containerRef = useRef(null); 

    const selectedWorkerData = useMemo(() => workers.find(w => w.id === selectedWorkerId), [selectedWorkerId, workers]);
    const isWorkerActive = selectedWorkerData?.on_duty == 1;

    useEffect(() => {
        if (containerRef.current && containerRef.current.parentElement) {
            containerRef.current.parentElement.style.justifyContent = 'flex-start';
            containerRef.current.parentElement.style.paddingTop = '16px';
        }
    }, []);

    const getCurrentTimeStr = () => {
        const now = new Date();
        const h = String(now.getHours()).padStart(2, '0');
        const m = String(now.getMinutes()).padStart(2, '0');
        return `${h}:${m}`;
    };

    useEffect(() => {
        if (selectedWorkerData && selectedWorkerId !== prevWorkerIdRef.current) {
            if (selectedWorkerData.looms) {
                const loomIds = selectedWorkerData.looms.toString().split(',').map(s => s.replace(/[^0-9]/g, '').trim()).filter(s => s !== '');
                setSelectedLooms(loomIds);
            }

            if (selectedWorkerData.shift_end) {
                const convertTo24 = (timeStr) => {
                    if(!timeStr) return '';
                    const [time, modifier] = timeStr.split(' ');
                    let [hours, minutes] = time.split(':');
                    if (hours === '12') hours = '00';
                    if (modifier === 'PM') hours = parseInt(hours, 10) + 12;
                    return `${hours}:${minutes}`;
                };

                const todayStr = new Date().toDateString();
                const selectedDateStr = rangeStart ? rangeStart.toDateString() : todayStr;

                if (selectedDateStr === todayStr) {
                    setStartTime(getCurrentTimeStr());
                } else {
                    if (selectedWorkerData.shift_start) {
                        setStartTime(convertTo24(selectedWorkerData.shift_start));
                    }
                }
                setEndTime(convertTo24(selectedWorkerData.shift_end));
            }
            prevWorkerIdRef.current = selectedWorkerId;
        }
    }, [selectedWorkerData, selectedWorkerId, rangeStart]); 

    const toggleLoom = (loomId) => {
        setSelectedLooms(prev => prev.includes(loomId) ? prev.filter(id => id !== loomId) : [...prev, loomId]);
    };

    const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

    const handleDateClick = (day) => {
        const clickedDate = new Date(currentDateView.getFullYear(), currentDateView.getMonth(), day);
        if (!rangeStart || (rangeStart && rangeEnd)) {
            setRangeStart(clickedDate);
            setRangeEnd(null);
        } else {
            if (clickedDate < rangeStart) {
                setRangeStart(clickedDate); 
                setRangeEnd(null);
            } else {
                setRangeEnd(clickedDate);
            }
        }
    };

    const isDateSelected = (day) => {
        if (!rangeStart) return false;
        const current = new Date(currentDateView.getFullYear(), currentDateView.getMonth(), day);
        if (rangeEnd) return current >= rangeStart && current <= rangeEnd;
        return current.getTime() === rangeStart.getTime();
    };

    const changeMonth = (offset) => {
        const newDate = new Date(currentDateView.getFullYear(), currentDateView.getMonth() + offset, 1);
        setCurrentDateView(newDate);
    };

    const formatTime12 = (time24) => {
        if (!time24) return '';
        const [h, m] = time24.split(':');
        let hr = parseInt(h);
        const ampm = hr >= 12 ? 'PM' : 'AM';
        hr = hr % 12 || 12;
        return `${hr}:${m} ${ampm}`;
    };

    const formatDateSQL = (date) => {
        if(!date) return '';
        const offset = date.getTimezoneOffset() * 60000; 
        const localDate = new Date(date.getTime() - offset);
        return localDate.toISOString().split('T')[0];
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setError(null);
        
        if (!selectedWorkerId) { setError("Select absent worker."); return; }
        if (!badliName.trim()) { setError("Enter or select Badli Name."); return; }
        if (!rangeStart) { setError("Select dates."); return; }

        setIsSubmitting(true);

        const fromDateStr = formatDateSQL(rangeStart);
        const toDateStr = rangeEnd ? formatDateSQL(rangeEnd) : fromDateStr;

        let finalStartTime = startTime || getCurrentTimeStr();

        const payload = {
            worker_id: selectedWorkerId,
            badli_worker_id: selectedBadliWorkerId || null, 
            badli_name: badliName,
            start_time: formatTime12(finalStartTime), 
            end_time: formatTime12(endTime),     
            from_date: fromDateStr,             
            to_date: toDateStr                  
        };

        try {
            const res = await fetch(`${API_BASE}/assign_badli.php`, {
                method: 'POST', 
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const text = await res.text();
            try {
                const data = JSON.parse(text);
                if (data.status === 'success') { 
                    onSave(); 
                    onClose(); 
                } else { setError(data.message || "Failed."); }
            } catch(e) { setError("Server Error: " + text.substring(0,50)); }
        } catch(e) { setError("Network Error"); } 
        finally { setIsSubmitting(false); }
    };

    const daysInMonth = getDaysInMonth(currentDateView);
    const startDay = getFirstDayOfMonth(currentDateView);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const displayDateRange = () => {
        if(!rangeStart) return "SELECT DATES";
        const formatShortDate = (d) => d.toLocaleDateString('en-GB', {day:'numeric', month:'short'});
        if(!rangeEnd || rangeStart.getTime() === rangeEnd.getTime()) {
            return `${formatShortDate(rangeStart)} (1 DAY)`.toUpperCase();
        }
        const diffTime = Math.abs(rangeEnd.getTime() - rangeStart.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return `${formatShortDate(rangeStart)} - ${formatShortDate(rangeEnd)} (${diffDays} DAYS)`.toUpperCase();
    };

    return (
        <Modal 
            isOpen={true} 
            onClose={(e) => {
                if (window.speechSynthesis) window.speechSynthesis.cancel();
                onClose(e);
            }} 
            title="ASSIGN BADLI" 
            icon={UserX} 
            iconColor="#0F172A" 
            iconBg="#F8FAFC"
            bg="#FFFFFF" 
            maxWidth="420px"
        >
          <style>{`
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
`}</style>
            
            <div ref={containerRef} style={{ marginTop: '0px', zoom: 0.95, width: '100%', boxSizing: 'border-box' }}>
                {error && <div style={styles.errorBox}><AlertTriangle size={14}/> {error}</div>}
                {isWorkerActive && <div style={styles.warningBox}>Warning: {selectedWorkerData?.name} is currently ON DUTY.</div>}

                {/* 1. ABSENT WORKER CARD */}
                <div style={styles.premiumCard}>
                    <label style={styles.label}><User size={12} color="#64748B" /> ABSENT WORKER</label>
                    <select 
                        style={styles.input} 
                        value={selectedWorkerId} 
                        onChange={e => setSelectedWorkerId(e.target.value)}
                        onFocus={() => handleFocusVoice && handleFocusVoice(" जो वर्कर आज छुट्टी पर है, लिस्ट में से उसका नाम चुनें।")}
                    >
                        <option value="">-- SELECT ABSENT WORKER --</option>
                        {workers.filter(w => w.is_badli == 0).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                </div>

                {/* 2. BADLI WORKER CARD */}
                <div style={styles.premiumCard}>
                    <label style={styles.label}><UserX size={12} color="#64748B" /> SUBSTITUTE WORKER</label>
                    <div style={styles.inputGroup}>
                        <input 
                            type="text" 
                            style={styles.groupedInput} 
                            value={badliName} 
                            onChange={(e) => {
                                setBadliName(e.target.value);
                                setSelectedBadliWorkerId(''); 
                            }} 
                            onFocus={() => handleFocusVoice && handleFocusVoice(" उस वर्कर का नाम लिखें, या बाजू वाले बटन पर क्लिक करके चुनें जो छुट्टी वाले वर्कर की जगह आज ड्यूटी करेगा।")}
                            placeholder="ENTER NAME MANUALLY"
                        />
                        <select 
                            style={styles.groupedSelect}
                            value={selectedBadliWorkerId}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedBadliWorkerId(val);
                                if (val) {
                                    const w = workers.find(x => String(x.id) === String(val));
                                    if (w) setBadliName(w.name);
                                }
                            }}
                        >
                            <option value="">SELECT EXISTING</option>
                            {workers.filter(w => String(w.id) !== String(selectedWorkerId)).map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* 3. DATE & TIME RANGE CARD */}
                <div style={styles.premiumCard}>
                    <label style={styles.label}><Calendar size={12} color="#64748B" /> SELECT DATE RANGE</label>
                    <div 
                        style={styles.dateRangeDisplay}
                        onClick={(e) => handleVoiceClick ? handleVoiceClick(e, " कितने दिन के लिए बदली ड्यूटी देना चाहते हैं, कैलेंडर से वो दिन की तारीख चुनें।", () => setShowCalendar(!showCalendar), null) : setShowCalendar(!showCalendar)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={14} color="#10B981"/>
                            {displayDateRange()}
                        </div>
                        {showCalendar ? <ChevronUp size={16} color="#64748B"/> : <ChevronDown size={16} color="#64748B"/>}
                    </div>

                    {showCalendar && (
                        <div style={styles.calendarBox}>
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                                <button onClick={()=>changeMonth(-1)} type="button" style={styles.calBtn}><ChevronLeft size={16}/></button>
                                <span style={{fontWeight:'800', fontSize:'12px', color:'#0F172A', textTransform: 'uppercase', letterSpacing: '1px'}}>
                                    {monthNames[currentDateView.getMonth()]} {currentDateView.getFullYear()}
                                </span>
                                <button onClick={()=>changeMonth(1)} type="button" style={styles.calBtn}><ChevronRight size={16}/></button>
                            </div>
                            <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'4px', textAlign:'center'}}>
                                {['S','M','T','W','T','F','S'].map(d => <div key={d} style={styles.calDayHeader}>{d}</div>)}
                                {Array(startDay).fill(null).map((_, i) => <div key={`empty-${i}`} />)}
                                {Array(daysInMonth).fill(null).map((_, i) => {
                                    const day = i + 1;
                                    const isSel = isDateSelected(day);
                                    return (
                                        <div 
                                            key={day} onClick={() => handleDateClick(day)}
                                            style={{
                                                padding:'6px 2px', borderRadius:'6px', fontSize:'11px', fontWeight:'700', cursor:'pointer',
                                                background: isSel ? '#10B981' : 'transparent',
                                                color: isSel ? '#FFFFFF' : '#0F172A',
                                                border: isSel ? '1px solid #10B981' : '1px solid transparent',
                                                transition: 'all 0.2s ease',
                                                boxShadow: isSel ? '0 2px 6px rgba(16, 185, 129, 0.2)' : 'none'
                                            }}
                                        >
                                            {day}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '12px'}}>
                        <div>
                            <label style={{...styles.label, marginBottom: '6px'}}><Timer size={12} color="#64748B" /> START TIME</label>
                            <input type="time" style={styles.input} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                        </div>
                        <div>
                            <label style={{...styles.label, marginBottom: '6px'}}><Timer size={12} color="#64748B" /> END TIME</label>
                            <input type="time" style={styles.input} value={endTime} onChange={e=>setEndTime(e.target.value)} />
                        </div>
                    </div>
                </div>

                {/* 4. LOOMS ASSIGNMENT CARD */}
                <div style={styles.premiumCard}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                        <label style={{...styles.label, marginBottom: 0}}><Layers size={12} color="#64748B" /> ASSIGN LOOMS</label>
                        {selectedWorkerData?.looms && (
                            <button type="button" onClick={(e) => {
                                e.preventDefault();
                                const allLooms = selectedWorkerData.looms.toString().split(',').map(s => s.replace(/[^0-9]/g, '').trim()).filter(s => s !== '');
                                setSelectedLooms(allLooms);
                            }} style={styles.selectAllBtn}>
                                <CheckCircle size={10} color="#10B981" /> SELECT ALL
                            </button>
                        )}
                    </div>
                    <div style={{display:'flex', flexWrap:'wrap', gap:'6px'}}>
                        {selectedWorkerData?.looms ? selectedWorkerData.looms.split(',').map(l => (
                            <span key={l} onClick={()=>toggleLoom(l.trim())} style={{
                                fontSize:'10.5px', padding:'6px 12px', borderRadius:'8px', cursor:'pointer', fontWeight:'700',
                                background: selectedLooms.includes(l.trim()) ? '#ECFDF5' : '#F8FAFC',
                                color: selectedLooms.includes(l.trim()) ? '#059669' : '#64748B',
                                border: selectedLooms.includes(l.trim()) ? '1px solid #10B981' : '1px solid #E2E8F0',
                                transition: 'all 0.2s ease',
                                boxShadow: selectedLooms.includes(l.trim()) ? '0 2px 4px rgba(16, 185, 129, 0.1)' : 'none'
                            }}>{l}</span>
                        )) : <span style={{fontSize:'9px', color:'#94A3B8', fontWeight:'600', textTransform: 'uppercase'}}>Select an absent worker first</span>}
                    </div>
                </div>
                
                {/* 🌟 NAYA: COMPACT GREEN EXECUTE MODULE 🌟 */}
                <div style={{
                    background: 'linear-gradient(145deg, #FFFFFF, #F8FAFC)',
                    borderRadius: '16px',
                    padding: '12px',
                    boxShadow: '0 8px 25px -5px rgba(15, 23, 42, 0.05)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    marginBottom: '10px',
                    marginTop: '16px',
                    position: 'sticky',
                    bottom: '0',
                    zIndex: 10
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ background: '#ECFDF5', padding: '8px', borderRadius: '10px', border: '1px solid #A7F3D0' }}>
                                <UserX size={16} color="#10B981" strokeWidth={2.5} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                <span style={{ fontSize: '12px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.2px', textTransform: 'uppercase', lineHeight: '1.2' }}>
                                    {badliName ? badliName : 'NO BADLI SELECTED'}
                                </span>
                                <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    {displayDateRange()}
                                </span>
                            </div>
                        </div>
                        <span style={{ fontSize: '7.5px', background: '#ECFDF5', color: '#10B981', padding: '4px 6px', borderRadius: '6px', fontWeight: '800', letterSpacing: '0.5px', border: '1px solid #A7F3D0' }}>
                            AUTO SYNC
                        </span>
                    </div>

                    {/* PREMIUM GREEN COMPACT BUTTON */}
                    <button 
                        onClick={(e) => handleVoiceClick ? handleVoiceClick(e, " आपकी बदली ड्यूटी सेव की जा रही है।", handleSubmit, 'btn-save-badli') : handleSubmit(e)} 
                        disabled={isSubmitting} 
                        style={styles.premiumSubmitBtn}
                    >
                        <CheckCircle size={16} color="#FFFFFF" strokeWidth={2.5} />
                        <span>{isSubmitting ? 'CONFIRMING...' : 'EXECUTE BADLI ASSIGNMENT'}</span>
                    </button>
                </div>

            </div>
        </Modal>
    );
};

// --- STYLES ---
const styles = {
    premiumCard: { 
        background: '#FFFFFF', 
        padding: '12px', // Compact Padding
        borderRadius: '16px', // Compact Radius
        border: '1px solid rgba(226, 232, 240, 0.8)', 
        boxShadow: '0 4px 15px -3px rgba(15, 23, 42, 0.03)', 
        marginBottom: '12px', // Compact Margin
        position: 'relative',
        overflow: 'hidden'
    },
    label: { 
        fontSize: '8.5px', // Compact Font
        fontWeight: '800', 
        color: '#64748B', 
        display: 'block', 
        marginBottom: '6px', // Compact Margin
        textTransform:'uppercase', 
        letterSpacing: '0.6px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    input: { 
        width: '100%', 
        padding: '10px 12px', // Compact Padding
        borderRadius: '10px', 
        border: '1px solid #E2E8F0', 
        fontSize: '11px', 
        outline: 'none', 
        background: '#F8FAFC', 
        color:'#0F172A', 
        fontWeight:'700', 
        boxSizing:'border-box', 
        transition: 'all 0.3s ease', 
        boxShadow: 'inset 0 1px 3px rgba(15,23,42,0.01)'
    },
    inputGroup: { 
        display: 'flex', 
        border: '1px solid #E2E8F0', 
        borderRadius: '10px', // Compact Radius
        overflow: 'hidden', 
        background: '#F8FAFC',
        boxShadow: 'inset 0 1px 3px rgba(15,23,42,0.01)'
    },
    groupedInput: { 
        flex: 1, 
        padding: '10px 12px', // Compact Padding
        border: 'none', 
        background: 'transparent', 
        outline: 'none', 
        fontSize: '11px', 
        color: '#0F172A', 
        fontWeight:'700', 
        boxSizing: 'border-box' 
    },
    groupedSelect: { 
        width: '130px', // Slightly narrower
        border: 'none', 
        borderLeft: '1px solid #E2E8F0', 
        background: '#FFFFFF', 
        padding: '10px', // Compact Padding
        outline: 'none', 
        fontSize: '10px', 
        color: '#0F172A', 
        cursor: 'pointer', 
        fontWeight: '800',
        textTransform: 'uppercase'
    },
    calendarBox: { 
        marginTop:'10px', 
        border:'1px solid #E2E8F0', 
        borderRadius:'10px', 
        padding:'12px', 
        background:'#F8FAFC',
        boxShadow: 'inset 0 2px 6px rgba(15,23,42,0.01)'
    },
    calBtn: { 
        background:'#FFFFFF', 
        border:'1px solid #E2E8F0', 
        borderRadius:'8px', 
        padding:'6px', 
        cursor:'pointer', 
        color:'#0F172A', 
        boxShadow: '0 1px 4px rgba(15,23,42,0.02)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    calDayHeader: { 
        fontSize:'8px', 
        color:'#94A3B8', 
        fontWeight:'800', 
        marginBottom: '6px' 
    },
    dateRangeDisplay: { 
        fontSize:'11px', 
        fontWeight:'800', 
        color:'#0F172A', 
        background:'#F8FAFC', 
        padding:'10px 12px', // Compact Padding
        borderRadius:'10px', 
        border:'1px solid #E2E8F0', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: 'inset 0 1px 3px rgba(15,23,42,0.01)'
    },
    selectAllBtn: { 
        background: '#ECFDF5', 
        color: '#059669', 
        border: '1px solid #A7F3D0', 
        borderRadius: '6px', 
        padding: '4px 8px', 
        fontSize: '8.5px', 
        fontWeight: '800', 
        cursor: 'pointer', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '4px',
        textTransform: 'uppercase',
        transition: 'all 0.2s',
        boxShadow: '0 1px 3px rgba(16, 185, 129, 0.1)'
    }, 
    premiumSubmitBtn: {
        width: '100%', 
        padding: '12px', // Compact Padding
        background: 'linear-gradient(135deg, #10B981, #059669)', // 🟢 Premium Green
        color: '#FFFFFF', 
        border: 'none', 
        borderRadius: '12px', 
        fontWeight: '900', 
        cursor: 'pointer', 
        fontSize:'11.5px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '8px', 
        boxShadow: '0 6px 16px rgba(16, 185, 129, 0.25)',
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        transition: 'all 0.3s ease'
    },
    errorBox: { background:'rgba(239, 68, 68, 0.1)', color:'#DC2626', padding:'10px', borderRadius:'10px', fontSize:'11px', marginBottom:'12px', display:'flex', alignItems: 'center', gap:'6px', fontWeight:'800', border:'1px solid rgba(239, 68, 68, 0.2)', textTransform: 'uppercase' },
    warningBox: { background:'rgba(245, 158, 11, 0.1)', color:'#D97706', padding:'10px', borderRadius:'10px', fontSize:'11px', marginBottom:'12px', fontWeight:'800', border:'1px solid rgba(245, 158, 11, 0.2)', display:'flex', alignItems: 'center', gap:'6px', textTransform: 'uppercase' }
};

export default BadliModal;